<template>
  <div class="vivo">
    <!-- top bar (mockup app__top) -->
    <div class="row between" style="margin-bottom: 14px">
      <div>
        <div class="pagetitle">{{ $t("company.title") }}</div>
        <div class="crumb">{{ $t("common.company") }} / {{ $t("company.crumbSettings") }}</div>
      </div>
      <div class="searchbar grow" style="max-width: 260px">{{ $t("company.search") }}</div>
    </div>

    <div v-if="loading" class="state">{{ $t("company.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("company.error") }}</div>

    <template v-else>
      <!-- ===== 1 · company profile (editable) ===== -->
      <div class="panel">
        <div class="panel__head"><h4>🏢 {{ $t("company.profile.title") }}</h4></div>
        <div class="panel__body">
          <div v-if="profileSaved" class="note info" style="margin-bottom: 12px">
            <b>{{ $t("company.profile.savedTitle") }}</b>{{ $t("company.profile.savedMsg") }}
          </div>
          <div v-if="profileError" class="note" style="margin-bottom: 12px">
            <b>{{ $t("company.profile.errorTitle") }}</b>{{ $t("company.profile.errorMsg") }}
          </div>

          <div class="form">
            <div class="field">
              <label>{{ $t("company.f.name") }} <span class="req">*</span></label>
              <input
                class="input"
                :class="{ err: pErrors.name }"
                v-model="profile.name"
                :placeholder="$t('company.ph.name')"
              />
              <div v-if="pErrors.name" class="hint err">{{ profileFieldError('name') }}</div>
            </div>

            <div class="field">
              <label>{{ $t("company.f.code") }}</label>
              <input class="input mono" data-test="company-code" :value="companyCode" disabled />
            </div>

            <div class="field">
              <label>{{ $t("company.f.commercialReg") }}</label>
              <input class="input mono" v-model="profile.commercialReg" />
            </div>

            <div class="field">
              <label>{{ $t("company.f.taxId") }}</label>
              <input class="input mono" v-model="profile.taxId" />
            </div>

            <div class="field">
              <label>{{ $t("company.f.contactPhone") }}</label>
              <input
                class="input mono"
                v-model="profile.contactPhone"
                :placeholder="$t('company.ph.contactPhone')"
              />
            </div>

            <div class="field">
              <label>{{ $t("company.f.contactEmail") }}</label>
              <input
                class="input"
                :class="{ err: pErrors.contactEmail }"
                v-model="profile.contactEmail"
                :placeholder="$t('company.ph.contactEmail')"
              />
              <div v-if="pErrors.contactEmail" class="hint err">{{ profileFieldError('contactEmail') }}</div>
            </div>

            <div class="field">
              <label>{{ $t("company.f.employeesCount") }}</label>
              <!-- read-only on purpose: H1 — this is COMPUTED from the employee
                   table on every read, not a stored column (the stored
                   Company.employeesCount was never written and read 0 forever). -->
              <input class="input mono" data-test="employees-count" :value="fInt(employeesCount)" disabled />
            </div>
          </div>

          <div class="divider"></div>
          <button class="btn primary" type="button" :disabled="savingProfile" @click="saveProfile">
            💾 {{ $t("company.profile.save") }}
          </button>
        </div>
      </div>

      <!-- ===== 2 · HR users ===== -->
      <!-- H2 «مستخدمي hr بنسجلهم ازاي … ولو عاوز اضيف مستخدم مش لاقي زرار حفظ».
           This panel used to display THREE INVENTED PEOPLE (seedUsers() built
           names from the company's email domain) above a permanently-disabled
           button, because the hr-users API was INSURER_ADMIN-only. It now reads
           and writes the real logins: GET/POST /companies/:id/hr-users, which
           HR may call for its OWN company. -->
      <div class="panel mt">
        <div class="panel__head">
          <h4>👤 {{ $t("company.users.title") }}</h4>
          <button
            class="btn primary sm"
            type="button"
            data-test="hr-user-add"
            @click="focusNewUser"
          >
            ➕ {{ $t("company.users.add") }}
          </button>
        </div>
        <div class="note mt" style="margin: 12px 15px 0">
          <b>{{ $t("company.users.whatTitle") }}</b>{{ $t("company.users.whatNote") }}
        </div>
        <div class="panel__body p0">
          <div v-if="usersLoading" class="state">{{ $t("company.users.loading") }}</div>
          <div v-else-if="usersError" class="note" data-test="hr-users-error">
            {{ $t("company.users.errors." + usersError) }}
            <button class="btn ghost sm" type="button" @click="loadUsers">{{ $t("company.users.retry") }}</button>
          </div>
          <div v-else class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th>{{ $t("company.users.thName") }}</th>
                  <th>{{ $t("company.users.thEmail") }}</th>
                  <th>{{ $t("company.users.thStatus") }}</th>
                  <th>{{ $t("company.users.thLastLogin") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(u, i) in users" :key="u.id" data-test="hr-user-row">
                  <td>
                    <div class="u">
                      <span class="avatar" :class="avatarClass(i)">{{ initials(u.fullName) }}</span>
                      {{ u.fullName }}
                    </div>
                  </td>
                  <td class="mono">{{ u.email }}</td>
                  <td>
                    <span class="tag" :class="userStatusTag(u.status)">
                      <span class="dotc"></span> {{ userStatusLabel(u.status) }}
                    </span>
                  </td>
                  <td class="xs muted">{{ u.lastLoginAt ? fDateTime(u.lastLoginAt) : $t("company.users.neverLoggedIn") }}</td>
                </tr>
                <tr v-if="users.length === 0">
                  <td colspan="4">
                    <div class="state">{{ $t("company.users.empty") }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="panel__body">
            <h5 class="sm" style="margin-bottom: 8px">{{ $t("company.users.addTitle") }}</h5>

            <div v-if="userAdded" class="note info" data-test="hr-user-added" style="margin-bottom: 12px">
              <b>{{ $t("company.users.addedTitle") }}</b>{{ $t("company.users.addedMsg", { email: userAdded }) }}
            </div>
            <div v-if="addError" class="note" data-test="hr-user-error" style="margin-bottom: 12px">
              {{ $t("company.users.errors." + addError) }}
            </div>

            <div class="form three">
              <div class="field">
                <label>{{ $t("company.users.f.name") }} <span class="req">*</span></label>
                <input
                  ref="newUserName"
                  class="input"
                  :class="{ err: uErrors.fullName }"
                  data-test="hr-user-name"
                  v-model="newUser.fullName"
                  :placeholder="$t('company.users.ph.name')"
                />
                <div v-if="uErrors.fullName" class="hint err">{{ userFieldError('fullName') }}</div>
              </div>
              <div class="field">
                <label>{{ $t("company.users.f.email") }} <span class="req">*</span></label>
                <input
                  class="input"
                  :class="{ err: uErrors.email }"
                  data-test="hr-user-email"
                  v-model="newUser.email"
                  :placeholder="$t('company.users.ph.email')"
                />
                <div v-if="uErrors.email" class="hint err">{{ userFieldError('email') }}</div>
              </div>
              <div class="field">
                <!-- The form had NO password field, so even an enabled button
                     could not have created a login: CompanyHrDto requires one. -->
                <label>{{ $t("company.users.f.password") }} <span class="req">*</span></label>
                <input
                  class="input"
                  :class="{ err: uErrors.password }"
                  data-test="hr-user-password"
                  type="password"
                  autocomplete="new-password"
                  v-model="newUser.password"
                  :placeholder="$t('company.users.ph.password')"
                />
                <div v-if="uErrors.password" class="hint err">{{ userFieldError('password') }}</div>
                <div v-else class="hint">{{ $t("company.users.passwordHint") }}</div>
              </div>
            </div>

            <button
              class="btn primary mt"
              type="button"
              data-test="hr-user-save"
              :disabled="addingUser"
              @click="addUser"
            >
              {{ addingUser ? $t("company.users.saving") : $t("company.users.addBtn") }}
            </button>

            <div class="note info mt">
              <b>{{ $t("company.users.rolesTitle") }}</b>{{ $t("company.users.rolesNote") }}
            </div>
          </div>
        </div>
      </div>

      <!-- ===== 3 · change password ===== -->
      <div class="panel mt">
        <div class="panel__head"><h4>🔑 {{ $t("company.password.title") }}</h4></div>
        <div class="panel__body">
          <div v-if="passwordChanged" class="note info" style="margin-bottom: 12px">
            <b>{{ $t("company.password.changedTitle") }}</b>{{ $t("company.password.changedMsg") }}
          </div>
          <div v-if="passwordError" class="note" style="border-color: var(--v-danger); margin-bottom: 12px">
            <b>!</b>{{ passwordErrorMsg }}
          </div>

          <div class="form three">
            <div class="field">
              <label>{{ $t("company.password.f.current") }} <span class="req">*</span></label>
              <input
                class="input"
                type="password"
                :class="{ err: pwErrors.current }"
                v-model="password.current"
                :placeholder="$t('company.password.ph')"
              />
              <div v-if="pwErrors.current" class="hint err">{{ passwordFieldError('current') }}</div>
            </div>
            <div class="field">
              <label>{{ $t("company.password.f.next") }} <span class="req">*</span></label>
              <input
                class="input"
                type="password"
                :class="{ err: pwErrors.next }"
                v-model="password.next"
                :placeholder="$t('company.password.ph')"
              />
              <div v-if="pwErrors.next" class="hint err">{{ passwordFieldError('next') }}</div>
            </div>
            <div class="field">
              <label>{{ $t("company.password.f.confirm") }} <span class="req">*</span></label>
              <input
                class="input"
                type="password"
                :class="{ err: pwErrors.confirm }"
                v-model="password.confirm"
                :placeholder="$t('company.password.ph')"
              />
              <div v-if="pwErrors.confirm" class="hint err">{{ passwordFieldError('confirm') }}</div>
            </div>
          </div>

          <div class="hint mt">{{ $t("company.password.hint") }}</div>
          <button class="btn primary mt" type="button" :disabled="passwordChanging" @click="changePassword">
            🔒 {{ passwordChanging ? $t("company.password.submitting") : $t("company.password.submit") }}
          </button>
        </div>
      </div>

      <!-- ===== 4 · company chatbot Q&A (C15) — linked here, not the sidebar,
           since it is a sub-feature of company settings ===== -->
      <div class="panel mt">
        <div class="panel__head"><h4>💬 {{ $t("company.qa.title") }}</h4></div>
        <div class="panel__body">
          <p class="xs muted">{{ $t("company.qa.body") }}</p>
          <router-link class="btn soft sm mt" :to="{ name: 'chatbot-qa' }">
            {{ $t("company.qa.link") }}
          </router-link>
        </div>
      </div>

      <div class="note info mt">
        <b>{{ $t("company.note.title") }}</b>{{ $t("company.note.body") }}
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPatch, apiPost } from "../../api/vivo/http";
import { companyPath } from "../../api/vivo/session";
import {
  userStatusTag,
  avatarClass,
  initials,
  validateHrUser,
  hrUserErrorKey,
  buildProfileForm,
  buildProfilePayload,
  validateProfile,
  validatePassword,
} from "./company-vm";

export default {
  name: "VivoCompany",
  data() {
    return {
      loading: true,
      error: false,
      company: null,
      profile: buildProfileForm(null),
      pErrors: {},
      savingProfile: false,
      profileSaved: false,
      profileError: false,
      users: [],
      usersLoading: true,
      usersError: "",
      newUser: { fullName: "", email: "", password: "" },
      uErrors: {},
      userAdded: "",
      addError: "",
      addingUser: false,
      password: { current: "", next: "", confirm: "" },
      pwErrors: {},
      passwordChanged: false,
      passwordChanging: false,
      passwordError: false,
      passwordErrorMsg: "",
    };
  },
  async mounted() {
    // HR manages its OWN company — derive the id from the JWT-backed session
    // (companyPath() → /companies/<companyId>), never a route param or a hardcoded
    // "1" fallback (which for HR-A pointed at a foreign company → 404/empty screen).
    try {
      this.company = await apiGet(companyPath());
      this.profile = buildProfileForm(this.company);
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
    // Separate from the profile load on purpose: if the logins cannot be read,
    // the rest of the screen must still work, so that failure is scoped to its
    // own panel instead of blanking the page.
    await this.loadUsers();
  },
  computed: {
    companyCode() {
      return this.company ? "CO-" + this.company.id : "—";
    },
    employeesCount() {
      return this.company ? this.company.employeesCount || 0 : 0;
    },
  },
  methods: {
    userStatusTag(status) {
      return userStatusTag(status);
    },
    avatarClass(index) {
      return avatarClass(index);
    },
    initials(name) {
      return initials(name);
    },
    fInt(n) {
      return new Intl.NumberFormat(this.$i18n.locale, { numberingSystem: "latn" }).format(n || 0);
    },
    profileFieldError(field) {
      const code = this.pErrors[field];
      return code ? this.$t("company.err." + code) : "";
    },
    userFieldError(field) {
      const code = this.uErrors[field];
      return code ? this.$t("company.err." + code) : "";
    },
    passwordFieldError(field) {
      const code = this.pwErrors[field];
      return code ? this.$t("company.err." + code) : "";
    },
    async saveProfile() {
      this.profileSaved = false;
      this.profileError = false;
      const result = validateProfile(this.profile);
      this.pErrors = result.errors;
      if (!result.valid) return;
      this.savingProfile = true;
      try {
        const updated = await apiPatch(companyPath(), buildProfilePayload(this.profile));
        // Adopt the response. `employeesCount` is computed server-side now, so
        // a save is the cheapest moment to refresh it; discarding the body left
        // the field showing whatever the first GET returned.
        if (updated && updated.id) this.company = updated;
        this.profileSaved = true;
      } catch (e) {
        this.profileError = true;
      } finally {
        this.savingProfile = false;
      }
    },
    focusNewUser() {
      const el = this.$refs.newUserName;
      if (el && typeof el.focus === "function") el.focus();
    },
    async loadUsers() {
      this.usersLoading = true;
      this.usersError = "";
      try {
        const rows = await apiGet(companyPath("hr-users"));
        this.users = Array.isArray(rows) ? rows : (rows && rows.data) || [];
      } catch (e) {
        this.usersError = hrUserErrorKey(e);
        this.users = [];
      } finally {
        this.usersLoading = false;
      }
    },
    userStatusLabel(status) {
      const key = "company.users.status." + status;
      return this.$te(key) ? this.$t(key) : status || "\u2014";
    },
    fDateTime(iso) {
      if (!iso) return "\u2014";
      return new Intl.DateTimeFormat("ar-EG", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
    async addUser() {
      if (this.addingUser) return;
      this.userAdded = "";
      this.addError = "";
      const result = validateHrUser(this.newUser);
      this.uErrors = result.errors;
      if (!result.valid) return;
      this.addingUser = true;
      const email = String(this.newUser.email || "").trim().toLowerCase();
      try {
        await apiPost(companyPath("hr-users"), {
          fullName: String(this.newUser.fullName || "").trim(),
          email: email,
          password: this.newUser.password,
        });
        // Clear the password as soon as it has been sent — it should not linger
        // in component state (or a Vue devtools snapshot).
        this.newUser = { fullName: "", email: "", password: "" };
        this.uErrors = {};
        this.userAdded = email;
        await this.loadUsers();
      } catch (e) {
        this.addError = hrUserErrorKey(e);
      } finally {
        this.addingUser = false;
      }
    },
    // Wired to the real self-service endpoint (POST /auth/change-password,
    // already used by other first-login/change-password flows) — this acts on
    // the CALLER's own account (the logged-in HR user), which is exactly what
    // this screen's "current/new/confirm password" form models.
    async changePassword() {
      this.passwordChanged = false;
      this.passwordError = false;
      this.passwordErrorMsg = "";
      const result = validatePassword(this.password);
      this.pwErrors = result.errors;
      if (!result.valid) return;
      this.passwordChanging = true;
      try {
        await apiPost("/auth/change-password", {
          currentPassword: this.password.current,
          newPassword: this.password.next,
        });
        this.password = { current: "", next: "", confirm: "" };
        this.pwErrors = {};
        this.passwordChanged = true;
      } catch (e) {
        const body = e && e.response && e.response.data;
        this.passwordError = true;
        this.passwordErrorMsg = (body && body.message) || this.$t("company.password.error");
      } finally {
        this.passwordChanging = false;
      }
    },
  },
};
</script>
